import { Component, inject, Input, OnChanges } from '@angular/core';
import { RouterModule } from '@angular/router';
import { PermissionService } from '../../../../core/services/permission.service';
import { KEYCLOAK_ROLES } from '../../../../shared/constants/auth.constants';
import { DashboardSnapshot } from '../../models/dashboard.model';
import { FirstStep, firstSteps } from '../../utils/dashboard-first-steps';

interface StepView extends FirstStep {
  index: number;
  link: { href: string; label: string } | null;
}

// Primeiros passos (spec §12): no lugar dos widgets quando a empresa nunca recebeu pedido. Três
// passos (catálogo, canal, anúncios) marcados como feitos ou pendentes pelos dados do snapshot, cada
// um com o link da tela, se a pessoa tem acesso a ela. No celular o botão desce para baixo do texto.
@Component({
  selector: 'app-dashboard-first-steps',
  standalone: true,
  imports: [RouterModule],
  styles: [
    `
      :host {
        display: block;
        min-width: 0;
      }

      .fs-step {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        gap: 0.75rem 1rem;
      }

      .fs-mark {
        flex: 0 0 auto;
        width: 1.75rem;
        height: 1.75rem;
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 0.8125rem;
        font-weight: 600;
        border: 1px solid var(--bs-border-color, #dbdade);
        color: var(--bs-secondary-color, #6f6b7d);
      }

      .fs-mark--done {
        border-color: transparent;
        background: rgba(40, 199, 111, 0.16);
        color: #28c76f;
      }

      /* Texto e botão dividem a linha; sem espaço para os dois, o botão desce (nada passa da borda). */
      .fs-body {
        flex: 1 1 12rem;
        min-width: 0;
        overflow-wrap: anywhere;
      }

      .fs-action {
        flex: 0 0 auto;
      }

      @media (max-width: 767.98px) {
        .fs-action {
          margin-left: 2.75rem;
        }
      }
    `,
  ],
  template: `
    @if (steps.length) {
      <div class="card mb-4">
        <div class="card-header">
          <h5 class="card-title mb-1">Primeiros passos</h5>
          <p class="text-muted mb-0">Siga estes passos para registrar a primeira venda.</p>
        </div>
        <div class="card-body pt-2">
          @for (step of steps; track step.id) {
            <div
              class="fs-step py-3"
              [class.border-top]="!$first"
              data-step
              [attr.data-step-done]="step.done ? '' : null"
            >
              <span class="fs-mark" [class.fs-mark--done]="step.done" aria-hidden="true">
                @if (step.done) {
                  <i class="ti ti-check"></i>
                } @else {
                  {{ step.index }}
                }
              </span>
              <div class="fs-body">
                <div class="fw-semibold text-heading" data-step-title>{{ step.title }}</div>
                <span class="visually-hidden">{{ step.done ? 'Concluído.' : 'Pendente.' }}</span>
                <div class="text-muted" data-step-text>{{ step.text }}</div>
              </div>
              @if (step.link; as link) {
                <a
                  class="fs-action btn btn-sm"
                  [class.btn-primary]="!step.done && step.id !== 'catalog'"
                  [class.btn-label-secondary]="step.done || step.id === 'catalog'"
                  [routerLink]="link.href"
                  data-step-link
                  >{{ link.label }}</a
                >
              }
            </div>
          }
        </div>
      </div>
    }
  `,
})
export class DashboardFirstStepsComponent implements OnChanges {
  @Input() snapshot?: DashboardSnapshot;

  steps: StepView[] = [];

  private readonly permissions = inject(PermissionService);

  ngOnChanges(): void {
    this.steps = this.snapshot
      ? firstSteps(this.snapshot).map((step, i) => ({ ...step, index: i + 1, link: this.linkFor(step) }))
      : [];
  }

  private linkFor(step: FirstStep): StepView['link'] {
    switch (step.id) {
      case 'catalog':
        return this.permissions.has(KEYCLOAK_ROLES.PRODUCT) ? { href: '/produtos', label: 'Ver produtos' } : null;
      case 'channel':
        return this.permissions.has(KEYCLOAK_ROLES.INTEGRATION)
          ? { href: '/integracoes', label: step.done ? 'Ver integrações' : 'Conectar canal' }
          : null;
      case 'ads':
        return this.permissions.has(KEYCLOAK_ROLES.ADVERTISEMENT) ? { href: '/anuncios', label: 'Ver anúncios' } : null;
    }
  }
}
